/**
 * Compare Size widget — theme port of the Intelligems DS_01/DS_05 personalization.
 *
 * The markup is rendered server-side by snippets/size-compare.liquid. This script wires up the
 * behaviour the experiment's injected JS used to provide:
 *   - open the popup on a (trusted) click of the "Compare Size" trigger or a size option
 *   - switch the Standard/Thick tabs (swaps the visible card + hint)
 *   - "Choose Standard/Thick" clicks the real theme size radio (handled by assets/size-pdp.js),
 *     then closes the popup
 *   - the (hidden) quiz row defers to the existing .dcop-size-quiz__trigger
 *   - ARIA dialog keyboard support: focus moves into the popup on open and back on close,
 *     Tab is trapped inside while open, Escape closes
 *
 * No viewport gating — CSS handles the responsive bottom-sheet vs modal. No analytics; the
 * experiment's tracking stays in Intelligems.
 */
const ready = (callback) => {
  if (document.readyState !== 'loading') callback();
  else document.addEventListener('DOMContentLoaded', callback);
};

ready(() => {
  const root = document.querySelector('.size-compare');
  if (!root) return;

  // Move the popup to <body> so position:fixed is relative to the viewport and it stacks
  // above the rest of the page. The size selector lives inside ancestors that create
  // stacking/transform contexts (gallery sliders, etc.), which would otherwise trap the
  // fixed popup and let later page sections paint on top of it. Mirrors how the original
  // Intelligems build injected the popup directly into <body>.
  if (root.parentElement !== document.body) {
    document.body.appendChild(root);
  }

  const sizeSelection = document.querySelector('#dcop-size-selection');
  // The real theme radios our CTAs proxy to. assets/size-pdp.js handles selection on click.
  const standardOption = sizeSelection && sizeSelection.querySelector('.new-content .variant-option');
  const thickOption = sizeSelection && sizeSelection.querySelector('.dropdown-content > .variant-option');
  const quizTrigger = document.querySelector('.dcop-size-quiz__trigger');

  // ARIA dialog pattern: remember who opened the popup so focus can return there on close,
  // move focus inside on open, keep Tab cycling within the popup, and close on Escape.
  let lastFocused = null;

  const focusableInPopup = () => {
    const candidates = root.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    // offsetParent is null for elements hidden with display:none (e.g. the inactive card's CTA)
    return Array.from(candidates).filter((el) => el.offsetParent !== null);
  };

  const open = () => {
    lastFocused = document.activeElement;
    root.classList.add('size-compare--open');
    const closeButton = root.querySelector('.size-compare__close');
    if (closeButton) closeButton.focus();
  };

  const close = () => {
    root.classList.remove('size-compare--open');
    if (lastFocused && document.contains(lastFocused) && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
    lastFocused = null;
  };

  document.addEventListener('keydown', (event) => {
    if (!root.classList.contains('size-compare--open')) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key !== 'Tab') return;
    const focusable = focusableInPopup();
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!root.contains(document.activeElement)) {
      // Focus escaped the popup (or never entered) — pull it back in.
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  const activateTab = (size) => {
    root.querySelectorAll('.size-compare__tab').forEach((tab) => {
      tab.classList.toggle('size-compare__tab--active', tab.dataset.size === size);
    });
    root.querySelectorAll('.size-compare__card').forEach((card) => {
      card.classList.toggle('size-compare__card--active', card.dataset.size === size);
    });
    root.querySelectorAll('.size-compare__hint-content').forEach((hint) => {
      hint.classList.toggle('size-compare__hint-content--active', hint.dataset.size === size);
    });
  };

  // Clicks inside the popup
  root.addEventListener('click', (event) => {
    const tab = event.target.closest('.size-compare__tab');
    const dismiss = event.target.closest('.size-compare__close, .size-compare__overlay');
    const cta = event.target.closest('.size-compare__cta');
    const quiz = event.target.closest('.size-compare__quiz span');

    if (tab) {
      activateTab(tab.dataset.size);
    } else if (dismiss) {
      close();
    } else if (cta) {
      const standardActive = root.querySelector('.size-compare__tab--standard.size-compare__tab--active');
      if (standardActive) {
        if (standardOption) standardOption.click();
      } else if (thickOption) {
        thickOption.click();
      }
      close();
    } else if (quiz && quizTrigger) {
      quizTrigger.click();
      close();
      quizTrigger.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  // Open the popup on a genuine user click of the trigger or a size option. isTrusted guards
  // against the programmatic .click() above re-opening the popup right after "Choose ...".
  document.addEventListener('click', (event) => {
    if (!event.isTrusted) return;
    if (event.target.closest('.size-compare__trigger')) {
      open();
    } else if (event.target.closest('#dcop-size-selection .new-content .variant-option')) {
      activateTab('Standard');
      open();
    } else if (event.target.closest('#dcop-size-selection .dropdown-content > .variant-option')) {
      activateTab('Thick');
      open();
    }
  });
});
